import React, {memo, useEffect, useRef} from 'react';

import {useSelectedCounts} from '../../state/graphql/selectedCountQuery';
import * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import * as ViewHooks from '../../state/views/hooks';
import * as RunSetTypes from '../../state/views/runSet/types';
import * as TempSelectionsActions from '../../state/views/tempSelections/actions';
import * as TempSelectionsTypes from '../../state/views/tempSelections/types';
import * as RunTypes from '../../util/runTypes';
import * as SM from '../../util/selectionmanager';
import {MultiStateCheckbox} from '../MultiStateCheckbox';
import {RunSelectAll} from '../RunSelectAll';
import {DisabledFeaturesMap} from './types';

export interface RunsTableHeaderProps {
  totalRowCount?: number;
  displayedRows: RunTypes.Run[];
  runSetRef: RunSetTypes.Ref;
  tempSelectionsRef: TempSelectionsTypes.Ref;
  customRunNamesRef?: CustomRunNamesTypes.Ref;
  showTempSelection: boolean;
  onlyShowSelected?: boolean;
  isSingleMode?: boolean;
  isInReport?: boolean;
  disabledFeatures?: DisabledFeaturesMap;
  onAllItemsTempSelected(): void;
  onAllItemsTempUnselected(): void;
  onVisibilitySelectAll(): void;
  onVisibilitySelectNone(): void;
  onSetOnlyShowSelected(newSetting: boolean): void;
}

const RunsTableHeaderComp = (props: RunsTableHeaderProps) => {
  const prevRowCountRef = useRef(props.totalRowCount);
  useEffect(() => {
    prevRowCountRef.current = props.totalRowCount;
  });
  const selectedCounts = useSelectedCounts(
    props.runSetRef,
    props.tempSelectionsRef,
    props.customRunNamesRef,
    prevRowCountRef.current !== props.totalRowCount
  );

  const runSetPart = ViewHooks.usePart(props.runSetRef);
  const tempSelectNone = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectNone,
    props.tempSelectionsRef,
    runSetPart.groupSelectionsRef
  );
  const tempSelectSome = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectSome,
    props.tempSelectionsRef,
    runSetPart.groupSelectionsRef,
    props.displayedRows
  );
  const tempSelectAllVisible = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectAllVisible,
    props.tempSelectionsRef,
    runSetPart.groupSelectionsRef
  );

  const groupSelections = ViewHooks.usePart(runSetPart.groupSelectionsRef);
  const tempSelections = ViewHooks.usePart(props.tempSelectionsRef);
  const groupTempSelections = {
    grouping: groupSelections.grouping,
    selections: tempSelections,
    expandedRowAddresses: groupSelections.expandedRowAddresses,
  };
  const rootTempCheckedState = SM.getCheckedState(
    groupTempSelections,
    {} as RunTypes.Run,
    0
  );

  const isCheckboxDisabled =
    props.disabledFeatures?.tag &&
    props.disabledFeatures?.move &&
    props.disabledFeatures?.delete;

  return (
    <>
      <div style={{width: 16}} />
      {!isCheckboxDisabled && props.showTempSelection && (
        <MultiStateCheckbox
          data-test="select-all-runs-checkbox"
          className="row-index-or-checkbox"
          checked={rootTempCheckedState}
          onClick={() => {
            if (rootTempCheckedState !== 'unchecked') {
              tempSelectNone();
              props.onAllItemsTempUnselected();
            } else {
              tempSelectSome();
              props.onAllItemsTempSelected();
            }

            const actionLocation = props.isInReport ? 'report' : 'runs table';
            window.analytics?.track('Select all runs clicked', {
              location: actionLocation,
            });
          }}
        />
      )}
      {!props.isSingleMode &&
        !props.disabledFeatures?.['toggle-visibility'] && (
          <RunSelectAll
            allCheckedState={SM.getCheckedState(
              groupSelections,
              {} as RunTypes.Run,
              0
            )}
            onSelectAll={props.onVisibilitySelectAll}
            onSelectNone={props.onVisibilitySelectNone}
            onlyShowSelected={props.onlyShowSelected}
            isInReport={props.isInReport}
            selectAllVisible={tempSelectAllVisible}
            setOnlyShowSelected={props.onSetOnlyShowSelected}
          />
        )}
      <span>
        Name&nbsp;{''}
        <span className="selected-count">
          {selectedCounts.tempSelectedCount > 0
            ? `(${selectedCounts.tempSelectedCount} selected)`
            : `(${selectedCounts.selectedCount} visualized)`}
        </span>
      </span>
    </>
  );
};

export const RunsTableHeader = memo(RunsTableHeaderComp);
